'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type { UpdateApplicationRequestDto } from '@terra/shared-contracts-applications';
import { toasterApi, type BusinessUnitOption } from '@terra/ui-design-system';
import {
  useApplicationBusinessUnits,
  useBusinessUnits,
  useCreateBusinessUnitOption,
  useUpdateApplicationBusinessUnits,
} from '../../../hooks/api/useBusinessUnits';
import { useUpdateApplicationFields } from '../../../hooks/api/useUpdateApplicationFields';

export function useApplicationGeneralFields({
  applicationId,
}: {
  applicationId: string | undefined;
}) {
  const t = useTranslations('designSystem.applicationGeneral');
  const updateApplicationFields = useUpdateApplicationFields();
  const { data: allBusinessUnits = [] } = useBusinessUnits({
    enabled: Boolean(applicationId),
  });
  const { data: assignedBusinessUnits = [] } = useApplicationBusinessUnits({
    applicationId,
  });
  const updateBusinessUnits = useUpdateApplicationBusinessUnits({
    applicationId,
  });
  const { createBusinessUnitOption, isCreatingBusinessUnit } =
    useCreateBusinessUnitOption();

  const runWithToast = useCallback(
    async (action: () => Promise<unknown>): Promise<boolean> => {
      try {
        await action();
        toasterApi.success({ title: t('saveSuccess') });

        return true;
      } catch (error: unknown) {
        toasterApi.error({
          title: t('saveError'),
          description: error instanceof Error ? error.message : undefined,
        });

        return false;
      }
    },
    [t],
  );

  const saveFields = useCallback(
    (body: Partial<UpdateApplicationRequestDto>): Promise<boolean> => {
      if (!applicationId) {
        return Promise.resolve(false);
      }

      return runWithToast(() =>
        updateApplicationFields.mutateAsync({ applicationId, body }),
      );
    },
    [applicationId, runWithToast, updateApplicationFields],
  );

  const saveBusinessUnits = useCallback(
    (businessUnits: BusinessUnitOption[]): Promise<boolean> =>
      runWithToast(() =>
        updateBusinessUnits.mutateAsync(businessUnits.map(({ id }) => id)),
      ),
    [runWithToast, updateBusinessUnits],
  );

  return {
    saveFields,
    isSavingFields: updateApplicationFields.isPending,
    allBusinessUnits,
    assignedBusinessUnits,
    saveBusinessUnits,
    isSavingBusinessUnits: updateBusinessUnits.isPending,
    createBusinessUnitOption,
    isCreatingBusinessUnit,
  };
}
